/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  usePresentationStore,
  type TPresentation,
  type TPresentationStatus,
  type TSlide
} from "@/zapi-frontend/stores/presentation";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import $api from "@/zapi-frontend/shared/api/axios";
import { toast } from "sonner";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { useParams } from "react-router-dom";
import { debounce } from "@/zapi-frontend/utils/debounce";
import { useQueryClient } from "@tanstack/react-query";

export type TGammaStatusResponse = {
  exists: boolean;
  gammaStatus: "generating" | "completed" | "failed";
  gammaWebUrl: string | null;
  gammaPptxUrl: string | null;
  gammaPdfUrl: string | null;
};

export type TSlidesStatusResponse = {
  exists: boolean;
  presentationStatus: "generating" | "completed" | "failed" | null;
};

export type TGenerateSlidesResponse = {
  id: number;
  projectId: number;
  status: TPresentationStatus;
  slides: Array<{ id: string; title: string; content: string; order: number }>;
};

export function usePresentation(options?: { enabled?: boolean }) {
  const { enabled = true } = options || {};
  const queryClient = useQueryClient();

  const {
    presentation,
    localPresentation,
    isLoading,
    isDirty,
    isSaving,
    isGammaGenerating,
    isSlidesGenerating,
    isSlidesRegenerating,
    slidesUpdatedFromChat,
    setPresentation,
    setLocalPresentation,
    setIsLoading,
    setIsGammaGenerating,
    setIsSlidesGenerating,
    setIsSaving,
    setIsSlidesRegenerating,
    updateSlide,
    deleteSlide,
    reorderSlides,
    addSlide,
    markClean,
    hasPendingEmptySlides,
    reset: resetPresentationStore
  } = usePresentationStore();
  const { selectedProject, updateSelectedProjectPresentation } = useProjectStore();
  const { id } = useParams();
  const projectId = id;
  const gammaPollingRef = useRef<NodeJS.Timeout>();
  const slidesPollingRef = useRef<NodeJS.Timeout>();
  const saveAbortControllerRef = useRef<AbortController | null>(null);

  const [isClearing, setIsClearing] = useState(false);

  const saveSlides = useCallback(async () => {
    const currentLocalPresentation = usePresentationStore.getState().localPresentation;
    if (!projectId || !currentLocalPresentation) return;

    if (saveAbortControllerRef.current) saveAbortControllerRef.current.abort();

    saveAbortControllerRef.current = new AbortController();
    const { signal } = saveAbortControllerRef.current;

    setIsSaving(true);

    const [, error] = await tryCatch(
      $api.post(
        `/projects/${projectId}/presentations/save`,
        {
          slides: currentLocalPresentation.slides.map((slide, index) => ({
            title: slide.title,
            content: slide.outline,
            order: index + 1
          })),
          gammaWebUrl: currentLocalPresentation.gammaWebUrl,
          gammaPptxUrl: currentLocalPresentation.gammaPptxUrl
        },
        { signal }
      )
    );

    if (signal.aborted) return;

    setIsSaving(false);

    if (error) {
      const isCanceled = error.name === "CanceledError" || (error as { code?: string }).code === "ERR_CANCELED";
      if (!isCanceled) toast.error("Failed to save presentation");
      return;
    }

    setPresentation(currentLocalPresentation);
    markClean();

    updateSelectedProjectPresentation({
      status: "completed",
      gammaWebUrl: currentLocalPresentation.gammaWebUrl,
      gammaPptxUrl: currentLocalPresentation.gammaPptxUrl,
      slidesCount: currentLocalPresentation.slides.length
    });
  }, [projectId, setIsSaving, setPresentation, markClean, updateSelectedProjectPresentation]);

  const debouncedSave = useMemo(() => debounce(saveSlides, 1000), [saveSlides]);

  const handleUpdateSlide = useCallback(
    (slideId: string, updates: Partial<TSlide>) => {
      updateSlide(slideId, updates);
      debouncedSave();
    },
    [updateSlide, debouncedSave]
  );

  const handleDeleteSlide = useCallback(
    (slideId: string) => {
      deleteSlide(slideId);
      debouncedSave();
    },
    [deleteSlide, debouncedSave]
  );

  const handleAddSlide = useCallback(
    (afterIndex?: number) => {
      const newSlideId = addSlide(afterIndex);
      debouncedSave();
      return newSlideId;
    },
    [addSlide, debouncedSave]
  );

  const handleReorderSlides = useCallback(
    (newOrder: TSlide[]) => {
      reorderSlides(newOrder);
      debouncedSave();
    },
    [reorderSlides, debouncedSave]
  );

  useEffect(() => {
    if (!enabled) return;

    return () => {
      if (saveAbortControllerRef.current) saveAbortControllerRef.current.abort();
    };
  }, []);

  const presentationExists = Boolean(selectedProject?.presentation);
  const slidesStatus = selectedProject?.presentation?.status || null;

  async function fetchPresentation() {
    if (!projectId || isLoading || !presentationExists) return;

    setIsLoading(true);
    const [response, error] = await tryCatch($api.get<{ data: TPresentation }>(`/projects/${projectId}/presentations`));
    setIsLoading(false);

    if (error) {
      toast.error("Error loading presentation");
      return;
    }

    if (response?.data?.data) {
      const data = response.data.data;
      const presentationData: TPresentation = {
        id: data.id,
        projectId: data.projectId,
        status: data.status,
        gammaStatus: data.gammaStatus,
        gammaWebUrl: data.gammaWebUrl,
        gammaPptxUrl: data.gammaPptxUrl,
        gammaPdfUrl: data.gammaPdfUrl,
        slidesGeneratedAt: data.slidesGeneratedAt,
        slidesUpdatedAt: data.slidesUpdatedAt,
        gammaGeneratedAt: data.gammaGeneratedAt,
        allowRegeneration: data.allowRegeneration,
        slides: data.slides
      };

      setPresentation(presentationData);
      setLocalPresentation(presentationData);
      setIsGammaGenerating(data.gammaStatus === "generating");
    }
  }

  async function checkGammaStatus() {
    if (!projectId) return;

    const { presentation: currentPresentation, localPresentation: currentLocalPresentation } =
      usePresentationStore.getState();

    if (!currentPresentation) return;

    const [response, error] = await tryCatch(
      $api.get<{ data: TGammaStatusResponse }>(`/projects/${projectId}/presentations/gamma-status`)
    );

    if (error) {
      console.error("Error checking Gamma status:", error);
      return;
    }

    const data: TGammaStatusResponse = response?.data?.data;
    if (!data || !data.exists) return;

    const updatedPresentation = {
      ...currentPresentation,
      gammaStatus: data.gammaStatus,
      gammaWebUrl: data.gammaWebUrl,
      gammaPptxUrl: data.gammaPptxUrl,
      gammaPdfUrl: data.gammaPdfUrl
    };

    setPresentation(updatedPresentation);

    // Only update gamma fields in local presentation, preserve user's slide edits
    if (currentLocalPresentation) {
      setLocalPresentation({
        ...currentLocalPresentation,
        gammaStatus: data.gammaStatus,
        gammaWebUrl: data.gammaWebUrl,
        gammaPptxUrl: data.gammaPptxUrl,
        gammaPdfUrl: data.gammaPdfUrl
      });
    }

    updateSelectedProjectPresentation({
      status: currentPresentation.status,
      gammaWebUrl: data.gammaWebUrl,
      gammaPptxUrl: data.gammaPptxUrl,
      slidesCount: currentPresentation.slides.length
    });

    if (data.gammaStatus === "completed") {
      setIsGammaGenerating(false);

      queryClient.invalidateQueries({
        queryKey: ["exportHistory", projectId]
      });

      if (gammaPollingRef.current) {
        clearInterval(gammaPollingRef.current);
        gammaPollingRef.current = undefined;
      }
    } else if (data.gammaStatus === "failed") {
      setIsGammaGenerating(false);
      toast.error("Preview generation failed");

      if (currentPresentation)
        setPresentation({ ...currentPresentation, gammaStatus: "failed", allowRegeneration: true });

      if (currentLocalPresentation)
        setLocalPresentation({ ...currentLocalPresentation, gammaStatus: "failed", allowRegeneration: true });

      if (gammaPollingRef.current) {
        clearInterval(gammaPollingRef.current);
        gammaPollingRef.current = undefined;
      }
    }
  }

  async function checkSlidesStatus() {
    if (!projectId) return;
    if (slidesStatus !== "generating" && !isSlidesGenerating) return;

    const [response, error] = await tryCatch(
      $api.get<{ data: TSlidesStatusResponse }>(`/projects/${projectId}/presentations/status`)
    );

    if (error) {
      console.error("Error checking slides status:", error);
      return;
    }

    const data = response?.data?.data;
    if (!data) return;

    const status = data.presentationStatus;

    if (status === "completed") {
      setIsSlidesGenerating(false);
      updateSelectedProjectPresentation({
        status: "completed",
        gammaWebUrl: null,
        gammaPptxUrl: null,
        slidesCount: 0
      });

      if (slidesPollingRef.current) {
        clearInterval(slidesPollingRef.current);
        slidesPollingRef.current = undefined;
      }
    } else if (status === "failed") {
      setIsSlidesGenerating(false);
      toast.error("Slides generation failed");

      if (slidesPollingRef.current) {
        clearInterval(slidesPollingRef.current);
        slidesPollingRef.current = undefined;
      }
    }
  }

  async function generateSlides() {
    if (!projectId || isSlidesGenerating) return;

    setIsSlidesGenerating(true);

    const [response, error] = await tryCatch(
      $api.post<{ data: TGenerateSlidesResponse }>(`/projects/${projectId}/presentations/generate`)
    );

    if (error) {
      toast.error("Failed to generate slides");
      setIsSlidesGenerating(false);
      return;
    }

    const data = response?.data?.data;

    if (data?.slides && data.slides.length > 0) {
      const slides = data.slides.map((slide) => ({
        id: String(slide.id),
        title: slide.title,
        outline: slide.content,
        order: slide.order
      }));

      const now = new Date().toISOString();
      const newPresentation: TPresentation = {
        id: data.id,
        projectId: parseInt(projectId),
        status: "completed",
        gammaStatus: null,
        gammaWebUrl: null,
        gammaPptxUrl: null,
        gammaPdfUrl: null,
        slidesGeneratedAt: now,
        slidesUpdatedAt: now,
        gammaGeneratedAt: now,
        allowRegeneration: false,
        slides
      };

      setPresentation(newPresentation);
      setLocalPresentation(newPresentation);
      console.log("Setting local presentation", newPresentation);
      markClean();
      setIsSlidesGenerating(false);

      updateSelectedProjectPresentation({
        status: "completed",
        gammaWebUrl: null,
        gammaPptxUrl: null,
        slidesCount: slides.length
      });
    } else {
      updateSelectedProjectPresentation({
        status: "generating",
        gammaWebUrl: null,
        gammaPptxUrl: null,
        slidesCount: 0
      });
    }
  }

  async function generateGammaPreview() {
    if (!projectId || !presentation || isGammaGenerating) return;
    if (!presentation.slides.length) {
      toast.error("No slides to generate preview from");
      return;
    }

    setIsGammaGenerating(true);

    const [, error] = await tryCatch($api.post(`/projects/${projectId}/presentations/regenerate-gamma`));

    if (error) {
      toast.error("Failed to generate preview");
      setIsGammaGenerating(false);
      const failedPresentation = {
        ...presentation,
        gammaStatus: "failed" as const,
        gammaWebUrl: null,
        gammaPptxUrl: null,
        gammaPdfUrl: null,
        allowRegeneration: true
      };
      setPresentation(failedPresentation);
      setLocalPresentation(failedPresentation);
      return;
    }

    const updatedPresentation = {
      ...presentation,
      gammaStatus: "generating" as const,
      gammaWebUrl: null,
      gammaPptxUrl: null,
      gammaPdfUrl: null,
      allowRegeneration: false
    };
    setPresentation(updatedPresentation);
    setLocalPresentation(updatedPresentation);
  }

  async function clearPresentation() {
    if (!projectId || isClearing) return;

    setIsClearing(true);

    const [, error] = await tryCatch($api.delete(`/projects/${projectId}/presentations`));

    setIsClearing(false);

    if (error) {
      toast.error("Failed to clear presentation");
      return;
    }

    resetPresentationStore();

    updateSelectedProjectPresentation(null);

    toast.success("Presentation cleared");
  }

  useEffect(() => {
    if (!enabled) return;

    if (!presentationExists && !isSlidesGenerating) resetPresentationStore();
  }, [enabled, projectId, presentationExists]);

  useEffect(() => {
    if (!enabled) return;

    if (presentationExists && slidesStatus === "completed" && !slidesUpdatedFromChat) fetchPresentation();
  }, [enabled, presentationExists, slidesStatus, slidesUpdatedFromChat]);

  useEffect(() => {
    if (!enabled) {
      if (slidesPollingRef.current) {
        clearInterval(slidesPollingRef.current);
        slidesPollingRef.current = undefined;
      }
      return;
    }

    if (slidesPollingRef.current) {
      clearInterval(slidesPollingRef.current);
      slidesPollingRef.current = undefined;
    }

    if (!projectId) return;
    if (slidesStatus !== "generating" && !isSlidesGenerating) return;

    const poll = () => checkSlidesStatus();

    setTimeout(poll, 2000);
    slidesPollingRef.current = setInterval(poll, 5000);

    return () => {
      if (slidesPollingRef.current) {
        clearInterval(slidesPollingRef.current);
        slidesPollingRef.current = undefined;
      }
    };
  }, [enabled, projectId, slidesStatus, isSlidesGenerating]);

  useEffect(() => {
    if (!enabled) {
      if (gammaPollingRef.current) {
        clearInterval(gammaPollingRef.current);
        gammaPollingRef.current = undefined;
      }
      return;
    }
    if (gammaPollingRef.current) {
      clearInterval(gammaPollingRef.current);
      gammaPollingRef.current = undefined;
    }

    if (!projectId || !presentation?.slides.length) return;

    if (presentation?.gammaStatus === "failed" || presentation?.gammaStatus === "completed") {
      setIsGammaGenerating(false);
      return;
    }

    if (presentation?.gammaStatus === "generating") {
      setIsGammaGenerating(true);
      setTimeout(checkGammaStatus, 2000);
      gammaPollingRef.current = setInterval(checkGammaStatus, 5000);
    }

    return () => {
      if (gammaPollingRef.current) {
        clearInterval(gammaPollingRef.current);
        gammaPollingRef.current = undefined;
      }
    };
  }, [enabled, projectId, presentation?.slides.length, presentation?.gammaStatus]);

  async function savePresentation() {
    if (!projectId || !localPresentation) return;

    setIsSaving(true);

    const [, error] = await tryCatch(
      $api.post(`/projects/${projectId}/presentations/save`, {
        slides: localPresentation.slides.map((slide, index) => ({
          title: slide.title,
          content: slide.outline,
          order: index + 1
        })),
        gammaWebUrl: localPresentation.gammaWebUrl,
        gammaPptxUrl: localPresentation.gammaPptxUrl
      })
    );

    setIsSaving(false);

    if (error) {
      toast.error("Failed to save presentation");
      return;
    }

    setPresentation(localPresentation);
    markClean();

    updateSelectedProjectPresentation({
      status: "completed",
      gammaWebUrl: localPresentation.gammaWebUrl,
      gammaPptxUrl: localPresentation.gammaPptxUrl,
      slidesCount: localPresentation.slides.length
    });

    toast.success("Presentation saved successfully");
  }

  async function regenerateSlides() {
    if (!projectId || isSlidesRegenerating || isGammaGenerating) return;

    setIsSlidesRegenerating(true);
    setIsLoading(true);

    const [response, error] = await tryCatch(
      $api.post<{ data: { slides: Array<{ id: string; title: string; content: string; order: number }> } }>(
        `/projects/${projectId}/presentations/regenerate-slides`
      )
    );

    if (error) {
      toast.error("Failed to regenerate slides");
      setIsSlidesRegenerating(false);
      setIsLoading(false);
      return;
    }

    if (response?.data?.data?.slides) {
      const slides = response.data.data.slides.map((slide) => ({
        id: String(slide.id),
        title: slide.title,
        outline: slide.content,
        order: slide.order
      }));

      const now = new Date().toISOString();
      const newPresentation: TPresentation = {
        id: presentation?.id || 0,
        projectId: parseInt(projectId),
        status: "completed",
        gammaStatus: presentation?.gammaStatus || null,
        gammaWebUrl: presentation?.gammaWebUrl || null,
        gammaPptxUrl: presentation?.gammaPptxUrl || null,
        gammaPdfUrl: presentation?.gammaPdfUrl || null,
        slidesGeneratedAt: now,
        slidesUpdatedAt: now,
        gammaGeneratedAt: presentation?.gammaGeneratedAt || now,
        allowRegeneration: false,
        slides
      };

      setPresentation(newPresentation);
      console.log("Setting presentation", newPresentation);
      setLocalPresentation(newPresentation);
      console.log("Setting local presentation", newPresentation);
      markClean();

      updateSelectedProjectPresentation({
        status: "completed",
        gammaWebUrl: null,
        gammaPptxUrl: null,
        slidesCount: slides.length
      });

      toast.success("Slides regenerated successfully!");
    }

    setIsSlidesRegenerating(false);
    setIsLoading(false);
  }

  return {
    presentation,
    localPresentation,
    slides: localPresentation?.slides || [],

    isLoading,
    isSaving: isSaving || isDirty,
    isSlidesGenerating: isSlidesGenerating || slidesStatus === "generating",
    isSlidesRegenerating,
    isGammaGenerating,
    isClearing,

    presentationExists,
    hasSlides: (localPresentation?.slides?.length || 0) > 0,
    isDirty,
    canSave: isDirty && !hasPendingEmptySlides(),
    slidesGeneratedAt: presentation?.slidesGeneratedAt || null,
    slidesUpdatedAt: localPresentation?.slidesUpdatedAt || presentation?.slidesUpdatedAt || new Date().toISOString(),
    gammaGeneratedAt: localPresentation?.gammaGeneratedAt || presentation?.gammaGeneratedAt || new Date().toISOString(),
    allowRegeneration: localPresentation?.allowRegeneration ?? presentation?.allowRegeneration ?? false,

    isGammaFailed: presentation?.gammaStatus === "failed",
    isGammaCompleted: presentation?.gammaStatus === "completed",
    gammaPptxUrl: presentation?.gammaPptxUrl || null,
    gammaPdfUrl: presentation?.gammaPdfUrl || null,
    gammaWebUrl: presentation?.gammaWebUrl || null,

    updateSlide: handleUpdateSlide,
    deleteSlide: handleDeleteSlide,
    reorderSlides: handleReorderSlides,
    addSlide: handleAddSlide,

    generateSlides,
    regenerateSlides,
    savePresentation,
    clearPresentation,

    generateGammaPreview
  };
}
